import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ApprovalCard,
  BliksLoader,
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellHeader
} from '@bliks-io/ui';
import { useApproval } from '@/api/queries/useApprovals';
import { useApprovalAction } from '@/api/mutations/useApprovalActions';
import { toastApiError } from '@/api/mutations/toast-api-error';

/** What an approval's tasks are filed under (`approval:<id>`); null for any other task. */
export function approvalIdOfRef(ref: string | undefined): string | null {
  return ref?.startsWith('approval:') ? ref.slice('approval:'.length) || null : null;
}

interface ApprovalDialogProps {
  approvalId: string | null;
  onClose: () => void;
}

/**
 * A Freigabe opened from a task (plan 053, decision 6): the card with what
 * was frozen for review and the caller's actions, so a reviewer decides
 * here instead of jumping into the app. "Open" still leads to the record.
 */
export function ApprovalDialog({ approvalId, onClose }: ApprovalDialogProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useApproval(approvalId);
  const action = useApprovalAction();

  const run = (input: Parameters<typeof action.mutate>[0], done: string) =>
    action.mutate(input, {
      onSuccess: () => toast.success(t(done)),
      onError: (error) => toastApiError(error, 'approvals.actionFailed')
    });

  return (
    <Dialog open={approvalId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogShell size="md">
        <DialogShellHeader title={t('approvals.dialogTitle')} onClose={onClose} />
        <DialogShellBody>
          {isLoading || !data ? (
            <BliksLoader label={t('common.loading')} />
          ) : (
            <ApprovalCard
              approval={data.approval}
              canDecide={data.canDecide}
              canOverride={data.canOverride}
              canWithdraw={data.canWithdraw}
              isPending={action.isPending}
              onDecide={(verdict, comment) =>
                run(
                  { kind: 'decide', id: data.approval._id, verdict, comment },
                  verdict === 'approved' ? 'approvals.toast.approved' : 'approvals.toast.rejected'
                )
              }
              onOverride={(reason) =>
                run(
                  { kind: 'override', id: data.approval._id, reason },
                  'approvals.toast.overridden'
                )
              }
              onWithdraw={() =>
                run({ kind: 'withdraw', id: data.approval._id }, 'approvals.toast.withdrawn')
              }
              onOpenRecord={
                data.approval.url
                  ? () => window.open(data.approval.url, '_blank', 'noopener,noreferrer')
                  : undefined
              }
            />
          )}
        </DialogShellBody>
      </DialogShell>
    </Dialog>
  );
}
